import React from 'react';

const OrderDetailsModal = ({ orders: ordersHook, delivery }) => {
  const {
    selectedOrder, showOrderDetails, closeOrderDetails,
    showAssignModal, assignOrderId, openAssignModal, closeAssignModal, handleAssign,
    handleUpdateOrderStatus, handleCancelOrder, getStatusColor,
  } = ordersHook;
  const { deliveryPersons } = delivery;

  if (!showOrderDetails || !selectedOrder) return null;

  const latestPayment = selectedOrder.payments && selectedOrder.payments.length > 0
    ? [...selectedOrder.payments].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
    : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-bold text-gray-800">Order Details - #{selectedOrder.orderNumber}</h3>
            <button onClick={closeOrderDetails} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Customer Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">Customer Information</h4>
              <div className="space-y-2">
                <p><span className="font-medium">Full Name:</span> {selectedOrder.customer?.fullName || 'N/A'}</p>
                <p><span className="font-medium">Email:</span> {selectedOrder.customer?.email || 'N/A'}</p>
                <p><span className="font-medium">Phone:</span> {selectedOrder.customer?.phone || 'N/A'}</p>
                <p><span className="font-medium">Company:</span> {selectedOrder.customer?.company || 'N/A'}</p>
                <p><span className="font-medium">Date of Birth:</span> {selectedOrder.customer?.dateOfBirth ? new Date(selectedOrder.customer.dateOfBirth).toLocaleDateString() : 'N/A'}</p>
                <p><span className="font-medium">Gender:</span> {selectedOrder.customer?.gender || 'N/A'}</p>
                <p><span className="font-medium">Address:</span> {selectedOrder.customer?.address || 'N/A'}</p>
                <p><span className="font-medium">City:</span> {selectedOrder.customer?.city || 'N/A'}</p>
                <p><span className="font-medium">County:</span> {selectedOrder.customer?.county || 'N/A'}</p>
                <p><span className="font-medium">Postal Code:</span> {selectedOrder.customer?.postalCode || 'N/A'}</p>
                <p><span className="font-medium">Country:</span> {selectedOrder.customer?.country || 'N/A'}</p>
                <p><span className="font-medium">Preferred Language:</span> {selectedOrder.customer?.preferredLanguage || 'en'}</p>
                <p><span className="font-medium">Marketing Notifications:</span>
                  <span className={`ml-2 px-2 py-1 text-xs rounded-full ${selectedOrder.customer?.marketingConsent ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {selectedOrder.customer?.marketingConsent ? 'ENABLED' : 'DISABLED'}
                  </span>
                </p>
                <p><span className="font-medium">SMS Notifications:</span>
                  <span className={`ml-2 px-2 py-1 text-xs rounded-full ${selectedOrder.customer?.smsConsent ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {selectedOrder.customer?.smsConsent ? 'ENABLED' : 'DISABLED'}
                  </span>
                </p>
                {selectedOrder.customer?.coordinates && (
                  <p><span className="font-medium">Coordinates:</span> {selectedOrder.customer.coordinates}</p>
                )}
              </div>
            </div>

            {/* Order Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">Order Information</h4>
              <div className="space-y-2">
                <p><span className="font-medium">Order Number:</span> #{selectedOrder.orderNumber}</p>
                <p><span className="font-medium">Status:</span>
                  <span className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedOrder.status)}`}>
                    {selectedOrder.status}
                  </span>
                </p>
                <p><span className="font-medium">Total Amount:</span> KSh {selectedOrder.total?.toLocaleString() || '0'}</p>
                <p><span className="font-medium">Order Date:</span> {new Date(selectedOrder.createdAt).toLocaleDateString()}</p>
                <p><span className="font-medium">Payment Status:</span> {selectedOrder.paymentStatus || 'N/A'}</p>
                <p><span className="font-medium">Payment Method:</span> {selectedOrder.paymentMethod || latestPayment?.method || 'N/A'}</p>
                {latestPayment?.reference && (
                  <p><span className="font-medium">Payment Reference:</span> {latestPayment.reference}</p>
                )}
                {latestPayment?.failureReason && (
                  <p className="text-red-600"><span className="font-medium text-gray-800">Payment Failure:</span> {latestPayment.failureReason}</p>
                )}
              </div>
            </div>

            {/* Shipping Information */}
            {selectedOrder.shipping && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold text-gray-800 mb-4">Shipping / Location Information</h4>
                <div className="space-y-2">
                  <p><span className="font-medium">Address:</span> {selectedOrder.shipping.address || 'N/A'}</p>
                  <p><span className="font-medium">City:</span> {selectedOrder.shipping.city || 'N/A'}</p>
                  <p><span className="font-medium">County:</span> {selectedOrder.shipping.county || selectedOrder.shipping.region || 'N/A'}</p>
                  <p><span className="font-medium">Postal Code:</span> {selectedOrder.shipping.postalCode || 'N/A'}</p>
                  <p><span className="font-medium">Country:</span> {selectedOrder.shipping.country || 'N/A'}</p>
                  <p><span className="font-medium">Delivery Method:</span> {selectedOrder.shipping.method || 'N/A'}</p>
                  <p><span className="font-medium">Tracking #:</span> {selectedOrder.shipping.trackingNumber || 'N/A'}</p>
                  <p><span className="font-medium">Shipping Cost:</span> KSh {selectedOrder.shipping.cost?.toLocaleString() || '0'}</p>
                </div>
              </div>
            )}

            {/* Delivery Assignment */}
            {selectedOrder.orderAssignment && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold text-gray-800 mb-4">Delivery Assignment</h4>
                <div className="space-y-2">
                  <p><span className="font-medium">Delivery Person:</span> {selectedOrder.orderAssignment.deliveryPerson?.fullName || 'N/A'}</p>
                  <p><span className="font-medium">Phone:</span> {selectedOrder.orderAssignment.deliveryPerson?.phone || 'N/A'}</p>
                  <p><span className="font-medium">Assigned Date:</span> {new Date(selectedOrder.orderAssignment.assignedAt).toLocaleDateString()}</p>
                  <p><span className="font-medium">Estimated Days:</span> {selectedOrder.orderAssignment.estimatedDeliveryDays || 'N/A'}</p>
                </div>
              </div>
            )}

            {/* Cancellation Reason & Customer Feedback */}
            {(selectedOrder.cancellationReason || selectedOrder.customerDeliveryFeedback) && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold text-gray-800 mb-4">Cancellation & Feedback</h4>
                <div className="space-y-3">
                  {selectedOrder.cancellationReason && (
                    <div>
                      <p className="font-medium text-gray-700 mb-1">Cancellation Reason:</p>
                      <p className="text-sm text-gray-600 bg-red-50 p-3 rounded border border-red-200">{selectedOrder.cancellationReason}</p>
                    </div>
                  )}
                  {selectedOrder.customerDeliveryFeedback && (
                    <div>
                      <p className="font-medium text-gray-700 mb-1">Customer Feedback:</p>
                      <p className="text-sm text-gray-600 bg-blue-50 p-3 rounded border border-blue-200">{selectedOrder.customerDeliveryFeedback}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Internal Notes */}
            {selectedOrder.internalNotes && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold text-gray-800 mb-4">Internal Notes</h4>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{selectedOrder.internalNotes}</p>
              </div>
            )}
          </div>

          {/* Order Items */}
          <div className="mt-6">
            <h4 className="text-lg font-semibold text-gray-800 mb-4">Order Items</h4>
            <div className="bg-white border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {selectedOrder.orderItems?.map((item) => (
                    <tr key={item.id}>
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          <img src={item.product?.image || '/images/placeholder.jpg'} alt={item.product?.name} className="h-10 w-10 rounded-lg object-cover mr-3" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{item.product?.name || 'N/A'}</div>
                            <div className="text-sm text-gray-500">SKU: {item.product?.sku || 'N/A'}</div>
                            {item.selectedColor && (
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-gray-500">Color:</span>
                                <div className="flex items-center gap-1">
                                  <div className="w-3 h-3 rounded-full border border-gray-300" style={{ backgroundColor: item.selectedColorCode || '#000000' }} title={item.selectedColor}></div>
                                  <span className="text-xs text-gray-600">{item.selectedColor}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{item.quantity}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">KSh {item.price?.toLocaleString() || '0'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">KSh {(item.quantity * item.price)?.toLocaleString() || '0'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Assign modal */}
          {showAssignModal && assignOrderId && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-60 p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                <div className="p-4">
                  <h4 className="text-lg font-semibold mb-3">Assign Delivery Person</h4>
                  <div className="space-y-2 max-h-72 overflow-y-auto">
                    {deliveryPersons.map((person) => (
                      <button
                        key={person.id}
                        onClick={() => handleAssign(assignOrderId, person.id)}
                        className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                      >
                        <div className="font-medium">{person.fullName}</div>
                        <div className="text-sm text-gray-500">{person.phone} — {person.vehicleType || 'N/A'}</div>
                      </button>
                    ))}
                  </div>
                  <div className="flex justify-end mt-4">
                    <button onClick={closeAssignModal} className="px-4 py-2 bg-gray-200 rounded-lg">Close</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Order Actions */}
          <div className="mt-6">
            {/* Terminal State Warning */}
            {['DELIVERED', 'CANCELLED', 'REFUNDED'].includes(selectedOrder.status) && (
              <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-sm font-medium text-yellow-800">
                    This order is in a <strong>{selectedOrder.status}</strong> state and cannot be modified. Terminal states are final and cannot be changed.
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {!['DELIVERED', 'CANCELLED', 'REFUNDED'].includes(selectedOrder.status) && (
                  <>
                    {selectedOrder.status === 'PENDING' && (
                      <button
                        onClick={() => handleUpdateOrderStatus(selectedOrder.id, 'CONFIRMED')}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Confirm Order
                      </button>
                    )}

                    {selectedOrder.status === 'CONFIRMED' && (
                      <button
                        onClick={() => handleUpdateOrderStatus(selectedOrder.id, 'PROCESSING')}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Start Processing
                      </button>
                    )}

                    {selectedOrder.status === 'PROCESSING' && (
                      <button
                        onClick={() => openAssignModal(selectedOrder.id)}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Mark as Shipped (assign delivery)
                      </button>
                    )}

                    {selectedOrder.status === 'SHIPPED' && (
                      <>
                        <button
                          onClick={() => handleUpdateOrderStatus(selectedOrder.id, 'DELIVERED')}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Mark as Delivered
                        </button>

                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              const deliveryPersonId = e.target.value;
                              const deliveryDays = prompt('Enter estimated delivery days:', '3');
                              if (deliveryDays) {
                                handleUpdateOrderStatus(selectedOrder.id, 'SHIPPED', deliveryPersonId, parseInt(deliveryDays, 10));
                              }
                            }
                          }}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                          defaultValue=""
                        >
                          <option value="">Assign Delivery Person</option>
                          {deliveryPersons.map((person) => (
                            <option key={person.id} value={person.id}>{person.fullName} - {person.phone}</option>
                          ))}
                        </select>
                      </>
                    )}

                    <button
                      onClick={() => handleCancelOrder(selectedOrder.id)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel Order
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <button onClick={closeOrderDetails} className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsModal;
